import React from 'react';

import { User } from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import { userName } from '@src/gen/ai/h2o/user/v1/user_pb';
import { Workspace } from '@src/gen/ai/h2o/workspace/v1/workspace_pb';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '@shared/services/appstore/HTTPResponse';
import {
  activeWorkspaceValidationQueryOptions,
  useQueryMyWorkspaces,
  useQueryWorkspace,
} from '@shared/services/authz/authz.hooks';

export interface WorkspaceQueriesResult {
  activeWorkspace?: Workspace;
  activeWorkspaceValid?: boolean;
  activeWorkspaceInitialized: boolean;
  staleActiveWorkspace: boolean;
  defaultWorkspace?: Workspace;
}

interface UseWorkspaceQueriesParams {
  user: User;
  activeWorkspaceId?: string;
}

/**
 * Hook that manages the workspace queries the provider exposes to the whole app:
 * the validated active workspace (consumed by shell chrome above the guard) and
 * the user's personal (default) workspace. Guard-only gating flags live in
 * `useWorkspaceGuardStatus` instead.
 */
export const useWorkspaceQueries = (params: UseWorkspaceQueriesParams): WorkspaceQueriesResult => {
  const { user, activeWorkspaceId } = params;
  // Validate that the active workspace stored in sessionStorage is valid for the current session
  const activeWorkspaceQuery = useQueryWorkspace(
    user.isPublic ? undefined : activeWorkspaceId,
    activeWorkspaceValidationQueryOptions
  );

  const activeWorkspaceValid =
    !activeWorkspaceQuery || activeWorkspaceQuery.isLoading ? undefined : activeWorkspaceQuery.isSuccess; // if the value is undefined, the state is unknown yet

  // Fetch user's personal (default) workspace
  const defaultWorkspaceQuery = useQueryMyWorkspaces(
    {
      filter: `personal_workspace_subject='${userName.compile({ user: user.id })}'`,
    },
    {
      enabled: !user.isPublic,
    }
  );
  const defaultWorkspace = defaultWorkspaceQuery.data?.pages[0]?.workspaces?.[0];

  // Track initialization state - once validated, it stays true
  const [activeWorkspaceInitialized, setActiveWorkspaceInitialized] = React.useState(false);

  React.useEffect(() => {
    if (activeWorkspaceValid) {
      // Once initialized, we never reset it back to false
      setActiveWorkspaceInitialized(true);
    }
  }, [activeWorkspaceValid]);

  const activeWorkspace = activeWorkspaceQuery?.data;

  const staleActiveWorkspace = Boolean(
    activeWorkspaceId &&
      activeWorkspaceValid === false &&
      activeWorkspaceQuery?.error &&
      isGrpcGatewayErrorResponse(activeWorkspaceQuery.error) &&
      (activeWorkspaceQuery.error.code === GrpcStatusCode.NOT_FOUND ||
        activeWorkspaceQuery.error.code === GrpcStatusCode.PERMISSION_DENIED)
  );

  return {
    activeWorkspace,
    activeWorkspaceValid,
    activeWorkspaceInitialized,
    staleActiveWorkspace,
    defaultWorkspace,
  };
};
